import { listBrainCaptureFolders } from "@afar-research/afar";
import type { EnvironmentId } from "@afar-research/contracts";
import { useEffect, useMemo, useState } from "react";

import { useProjectEntriesQuery } from "../files/projectFilesQueryState";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";

/**
 * Picks the capture folders a Delete Brain Operation removes. The vault has no
 * index and nothing counts citations, so a row says what the filesystem can
 * answer — the folder and how much it captured — and the wiki repair is left to
 * the operation rather than previewed here.
 */
export function BrainDeleteRawsDialog(props: {
  readonly open: boolean;
  readonly environmentId: EnvironmentId;
  readonly projectRoot: string;
  readonly selected: ReadonlyArray<string>;
  readonly onOpenChange: (open: boolean) => void;
  readonly onConfirm: (captureFolders: ReadonlyArray<string>) => void;
}) {
  return props.open ? <BrainDeleteRawsDialogSession {...props} /> : null;
}

function BrainDeleteRawsDialogSession(props: {
  readonly open: boolean;
  readonly environmentId: EnvironmentId;
  readonly projectRoot: string;
  readonly selected: ReadonlyArray<string>;
  readonly onOpenChange: (open: boolean) => void;
  readonly onConfirm: (captureFolders: ReadonlyArray<string>) => void;
}) {
  const entriesQuery = useProjectEntriesQuery(props.environmentId, props.projectRoot);
  const [search, setSearch] = useState("");
  const [selection, setSelection] = useState<ReadonlySet<string>>(new Set(props.selected));
  // The picker opens on what the chip row already carries, so reopening it
  // edits the selection instead of starting over.
  useEffect(() => {
    setSelection(new Set(props.selected));
  }, [props.selected]);

  const folders = useMemo(
    () => listBrainCaptureFolders(entriesQuery.data?.entries ?? []),
    [entriesQuery.data?.entries],
  );
  const needle = search.trim().toLowerCase();
  const visible = useMemo(
    () =>
      needle.length === 0
        ? folders
        : folders.filter((folder) => folder.name.toLowerCase().includes(needle)),
    [folders, needle],
  );

  const toggle = (name: string) => {
    setSelection((current) => {
      const next = new Set(current);
      if (!next.delete(name)) next.add(name);
      return next;
    });
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogPopup data-brain-delete-raws-dialog>
        <DialogHeader>
          <DialogTitle>Delete raw sources</DialogTitle>
          <DialogDescription>
            Each capture folder is removed whole, and every wiki page that cited it is repaired
            rather than deleted.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel>
          <div className="space-y-3">
            <Input
              value={search}
              placeholder="Filter capture folders…"
              aria-label="Filter capture folders"
              onChange={(event) => setSearch(event.target.value)}
            />
            {entriesQuery.error !== null ? (
              <div className="rounded-md border border-destructive/40 p-4 text-sm text-destructive">
                {entriesQuery.error}
              </div>
            ) : folders.length === 0 ? (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                {entriesQuery.isPending
                  ? "Reading the vault…"
                  : "This Brain has no raw captures to delete."}
              </div>
            ) : visible.length === 0 ? (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                No capture folder matches that filter.
              </div>
            ) : (
              <ul className="max-h-72 space-y-1 overflow-auto" aria-label="Raw capture folders">
                {visible.map((folder) => (
                  <li key={folder.name}>
                    <label className="flex w-full cursor-pointer items-center gap-3 rounded-md border p-2.5 text-left hover:bg-muted/50">
                      <Checkbox
                        checked={selection.has(folder.name)}
                        onCheckedChange={() => toggle(folder.name)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{folder.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          raw/{folder.name} · {folder.fileCount}{" "}
                          {folder.fileCount === 1 ? "file" : "files"}
                        </span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
            {entriesQuery.data?.truncated ? (
              <p className="text-xs text-muted-foreground">
                This project has more files than one listing carries, so a capture folder may be
                missing. Filter to narrow the vault, or delete in smaller batches.
              </p>
            ) : null}
          </div>
        </DialogPanel>
        <DialogFooter>
          <Button variant="ghost" onClick={() => props.onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              props.onConfirm(folders.filter((f) => selection.has(f.name)).map((f) => f.name));
              props.onOpenChange(false);
            }}
          >
            {selection.size > 0 ? `Select ${selection.size}` : "Select none"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
